import React, { useEffect, useState } from 'react';
import { DollarSignIcon, ChevronRightIcon, BookIcon, PlusIcon, RefreshCwIcon, SearchIcon, FilterIcon, SettingsIcon, UploadIcon } from 'lucide-react';
import { ExchangeRateTable } from './exchange/ExchangeRateTable';
import { ExchangeRateForm } from './exchange/ExchangeRateForm';
import { ExternalSubscriptionForm } from './exchange/ExternalSubscriptionForm';
import { RateHistoryView } from './exchange/RateHistoryView';
import { ExchangeRateUpload } from './exchange/ExchangeRateUpload';
// Define types
export type ExchangeRateType = 'Spot' | 'Contract' | 'Corporate' | 'Bank' | 'Custom';
export type EntityScope = 'Global' | 'Entity' | 'Site';
export interface ExchangeRate {
  id: string;
  fromCurrency: string;
  toCurrency: string;
  rate: number;
  inverseRate: number;
  type: ExchangeRateType;
  entityId?: string;
  entityName?: string;
  siteId?: string;
  siteName?: string;
  scope: EntityScope;
  isActive: boolean;
  lastUpdated: string;
  updatedBy?: string;
}
export interface RateHistory {
  id: string;
  rateId: string;
  fromCurrency: string;
  toCurrency: string;
  rate: number;
  date: string;
  type: ExchangeRateType;
  source: string;
}
export function CurrencyExchangeView({
  locale = 'en-US'
}) {
  // States
  const [exchangeRates, setExchangeRates] = useState<ExchangeRate[]>([]);
  const [filteredRates, setFilteredRates] = useState<ExchangeRate[]>([]);
  const [selectedRate, setSelectedRate] = useState<ExchangeRate | null>(null);
  const [showRateForm, setShowRateForm] = useState(false);
  const [showSubscriptionForm, setShowSubscriptionForm] = useState(false);
  const [showRateHistory, setShowRateHistory] = useState(false);
  const [showRateUpload, setShowRateUpload] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    fromCurrency: '',
    toCurrency: '',
    type: '',
    scope: '',
    status: '',
    searchTerm: ''
  });
  // Load sample data
  useEffect(() => {
    // In a real app, this would be an API call
    const sampleData: ExchangeRate[] = [{
      id: 'rate-001',
      fromCurrency: 'USD',
      toCurrency: 'EUR',
      rate: 0.92,
      inverseRate: 1.09,
      type: 'Spot',
      scope: 'Global',
      isActive: true,
      lastUpdated: '2023-10-20T08:30:00Z',
      updatedBy: 'System'
    }, {
      id: 'rate-002',
      fromCurrency: 'USD',
      toCurrency: 'GBP',
      rate: 0.78,
      inverseRate: 1.28,
      type: 'Spot',
      scope: 'Global',
      isActive: true,
      lastUpdated: '2023-10-20T08:30:00Z',
      updatedBy: 'System'
    }, {
      id: 'rate-003',
      fromCurrency: 'USD',
      toCurrency: 'CAD',
      rate: 1.37,
      inverseRate: 0.73,
      type: 'Spot',
      scope: 'Global',
      isActive: true,
      lastUpdated: '2023-10-20T08:30:00Z',
      updatedBy: 'System'
    }, {
      id: 'rate-004',
      fromCurrency: 'USD',
      toCurrency: 'AUD',
      rate: 1.55,
      inverseRate: 0.65,
      type: 'Custom',
      scope: 'Global',
      isActive: true,
      lastUpdated: '2023-10-18T14:25:00Z',
      updatedBy: 'John Smith'
    }, {
      id: 'rate-005',
      fromCurrency: 'EUR',
      toCurrency: 'GBP',
      rate: 0.87,
      inverseRate: 1.15,
      type: 'Spot',
      scope: 'Global',
      isActive: true,
      lastUpdated: '2023-10-20T08:30:00Z',
      updatedBy: 'System'
    }, {
      id: 'rate-006',
      fromCurrency: 'USD',
      toCurrency: 'JPY',
      rate: 149.75,
      inverseRate: 0.0067,
      type: 'Bank',
      scope: 'Global',
      isActive: true,
      lastUpdated: '2023-10-20T08:30:00Z',
      updatedBy: 'OpenExchangeRates API'
    }, {
      id: 'rate-007',
      fromCurrency: 'EUR',
      toCurrency: 'INR',
      rate: 90.32,
      inverseRate: 0.011,
      type: 'Bank',
      scope: 'Global',
      isActive: true,
      lastUpdated: '2023-10-20T08:30:00Z',
      updatedBy: 'OpenExchangeRates API'
    }, {
      id: 'rate-008',
      fromCurrency: 'USD',
      toCurrency: 'MXN',
      rate: 18.25,
      inverseRate: 0.055,
      type: 'Custom',
      entityId: 'entity-002',
      entityName: 'LATAM Operations',
      scope: 'Entity',
      isActive: true,
      lastUpdated: '2023-10-15T10:15:00Z',
      updatedBy: 'Maria Rodriguez'
    }, {
      id: 'rate-009',
      fromCurrency: 'EUR',
      toCurrency: 'USD',
      rate: 1.09,
      inverseRate: 0.92,
      type: 'Contract',
      entityId: 'entity-001',
      entityName: 'Corporate',
      siteId: 'site-003',
      siteName: 'European Office',
      scope: 'Site',
      isActive: true,
      lastUpdated: '2023-10-10T16:45:00Z',
      updatedBy: 'Contract #C-2023-056'
    }, {
      id: 'rate-010',
      fromCurrency: 'GBP',
      toCurrency: 'CAD',
      rate: 1.68,
      inverseRate: 0.6,
      type: 'Corporate',
      scope: 'Global',
      isActive: false,
      lastUpdated: '2023-09-30T11:20:00Z',
      updatedBy: 'Sarah Johnson'
    }];
    setExchangeRates(sampleData);
    setFilteredRates(sampleData);
  }, []);
  // Apply filters
  useEffect(() => {
    let result = [...exchangeRates];
    if (filters.fromCurrency) {
      result = result.filter(rate => rate.fromCurrency === filters.fromCurrency);
    }
    if (filters.toCurrency) {
      result = result.filter(rate => rate.toCurrency === filters.toCurrency);
    }
    if (filters.type) {
      result = result.filter(rate => rate.type === filters.type);
    }
    if (filters.scope) {
      result = result.filter(rate => rate.scope === filters.scope);
    }
    if (filters.status) {
      const isActive = filters.status === 'Active';
      result = result.filter(rate => rate.isActive === isActive);
    }
    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      result = result.filter(rate => rate.fromCurrency.toLowerCase().includes(term) || rate.toCurrency.toLowerCase().includes(term) || rate.entityName && rate.entityName.toLowerCase().includes(term) || rate.siteName && rate.siteName.toLowerCase().includes(term));
    }
    setFilteredRates(result);
  }, [exchangeRates, filters]);
  // Handle adding a new rate
  const handleAddRate = () => {
    setSelectedRate(null);
    setShowRateForm(true);
    setShowSubscriptionForm(false);
    setShowRateHistory(false);
  };
  // Handle editing a rate
  const handleEditRate = (rate: ExchangeRate) => {
    setSelectedRate(rate);
    setShowRateForm(true);
    setShowSubscriptionForm(false);
    setShowRateHistory(false);
  };
  // Handle viewing rate history
  const handleViewHistory = (rate: ExchangeRate) => {
    setSelectedRate(rate);
    setShowRateHistory(true);
    setShowRateForm(false);
    setShowSubscriptionForm(false);
  };
  // Handle toggling rate active status
  const handleToggleStatus = (rateId: string) => {
    setExchangeRates(prevRates => prevRates.map(rate => rate.id === rateId ? {
      ...rate,
      isActive: !rate.isActive,
      lastUpdated: new Date().toISOString(),
      updatedBy: 'Current User'
    } : rate));
  };
  // Handle saving a rate
  const handleSaveRate = (rate: ExchangeRate) => {
    if (rate.id) {
      // Update existing rate
      setExchangeRates(prevRates => prevRates.map(r => r.id === rate.id ? rate : r));
    } else {
      // Add new rate
      const newRate = {
        ...rate,
        id: `rate-${exchangeRates.length + 1}`.padStart(6, '0'),
        lastUpdated: new Date().toISOString(),
        updatedBy: 'Current User'
      };
      setExchangeRates(prevRates => [...prevRates, newRate]);
    }
    setShowRateForm(false);
  };
  // Handle filter changes
  const handleFilterChange = (name: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // Clear all filters
  const clearFilters = () => {
    setFilters({
      fromCurrency: '',
      toCurrency: '',
      type: '',
      scope: '',
      status: '',
      searchTerm: ''
    });
  };
  // Show external subscription settings
  const handleShowSubscriptionSettings = () => {
    setShowSubscriptionForm(true);
    setShowRateForm(false);
    setShowRateHistory(false);
  };
  // Handle running a sync now
  const handleRunSync = () => {
    // In a real app, this would trigger an API call to sync with external provider
    alert('Synchronization with external provider initiated.');
  };
  // Handle importing rates from upload
  const handleImportRates = (rates: ExchangeRate[]) => {
    // Check for existing rates with the same currency pairs
    const updatedRates = [...exchangeRates];
    rates.forEach(newRate => {
      const existingIndex = updatedRates.findIndex(rate => rate.fromCurrency === newRate.fromCurrency && rate.toCurrency === newRate.toCurrency && rate.scope === newRate.scope);
      if (existingIndex >= 0) {
        // Update existing rate
        updatedRates[existingIndex] = {
          ...updatedRates[existingIndex],
          rate: newRate.rate,
          inverseRate: newRate.inverseRate,
          type: newRate.type,
          isActive: newRate.isActive,
          lastUpdated: new Date().toISOString(),
          updatedBy: 'Import'
        };
      } else {
        // Add new rate
        updatedRates.push({
          ...newRate,
          id: `rate-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
        });
      }
    });
    setExchangeRates(updatedRates);
    setShowRateUpload(false);
    // Show success message
    alert(`Successfully imported ${rates.length} exchange rates.`);
  };
  return <div className="space-y-6" data-id="element-7191">
      <div className="mb-4" data-id="element-7192">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-7193">Currency Exchange</h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-7194">
          Manage exchange rates for multi-currency transactions across the
          procure-to-pay ecosystem
        </p>
      </div>

      {/* Breadcrumbs */}
      <nav className="flex mb-4" aria-label="Breadcrumb" data-id="element-7195">
        <ol className="inline-flex items-center space-x-1 md:space-x-3" data-id="element-7196">
          <li className="inline-flex items-center" data-id="element-7197">
            <button className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-800" data-id="element-7198">
              <BookIcon className="w-4 h-4 mr-2" data-id="element-7199" />
              Financial
            </button>
          </li>
          <li data-id="element-7200">
            <div className="flex items-center" data-id="element-7201">
              <ChevronRightIcon className="w-4 h-4 text-gray-400 mx-1" data-id="element-7202" />
              <span className="text-sm font-medium text-gray-500" data-id="element-7203">
                Currency Exchange
              </span>
            </div>
          </li>
        </ol>
      </nav>

      {/* Main content */}
      <div className="flex flex-col space-y-6 lg:flex-row lg:space-y-0 lg:space-x-6" data-id="element-7204">
        {/* Left column - Exchange Rate Table */}
        <div className="lg:w-2/3" data-id="element-7205">
          <div className="bg-white shadow rounded-lg" data-id="element-7206">
            <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-7207">
              <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center" data-id="element-7208">
                <DollarSignIcon className="h-5 w-5 mr-2 text-indigo-500" data-id="element-7209" />
                Exchange Rates
              </h3>
              <div className="flex items-center space-x-2" data-id="element-7210">
                <div className="relative rounded-md shadow-sm" data-id="element-7211">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7212">
                    <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-7213" />
                  </div>
                  <input type="text" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search rates..." value={filters.searchTerm} onChange={e => handleFilterChange('searchTerm', e.target.value)} data-id="element-7214" />
                </div>
                <button onClick={() => setIsFilterOpen(!isFilterOpen)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7215">
                  <FilterIcon className="h-4 w-4 mr-1" data-id="element-7216" />
                  Filters
                </button>
                <button onClick={clearFilters} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7217">
                  <RefreshCwIcon className="h-4 w-4 mr-1" data-id="element-7218" />
                  Reset
                </button>
              </div>
            </div>

            {/* Filters panel */}
            {isFilterOpen && <div className="px-4 py-5 sm:px-6 border-b border-gray-200 bg-gray-50" data-id="element-7219">
                <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6" data-id="element-7220">
                  <div className="sm:col-span-2" data-id="element-7221">
                    <label htmlFor="fromCurrency" className="block text-sm font-medium text-gray-700" data-id="element-7222">
                      From Currency
                    </label>
                    <select id="fromCurrency" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filters.fromCurrency} onChange={e => handleFilterChange('fromCurrency', e.target.value)} data-id="element-7223">
                      <option value="" data-id="element-7224">All</option>
                      <option value="USD" data-id="element-7225">USD</option>
                      <option value="EUR" data-id="element-7226">EUR</option>
                      <option value="GBP" data-id="element-7227">GBP</option>
                      <option value="CAD" data-id="element-7228">CAD</option>
                      <option value="AUD" data-id="element-7229">AUD</option>
                      <option value="JPY" data-id="element-7230">JPY</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2" data-id="element-7231">
                    <label htmlFor="toCurrency" className="block text-sm font-medium text-gray-700" data-id="element-7232">
                      To Currency
                    </label>
                    <select id="toCurrency" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filters.toCurrency} onChange={e => handleFilterChange('toCurrency', e.target.value)} data-id="element-7233">
                      <option value="" data-id="element-7234">All</option>
                      <option value="USD" data-id="element-7235">USD</option>
                      <option value="EUR" data-id="element-7236">EUR</option>
                      <option value="GBP" data-id="element-7237">GBP</option>
                      <option value="CAD" data-id="element-7238">CAD</option>
                      <option value="AUD" data-id="element-7239">AUD</option>
                      <option value="JPY" data-id="element-7240">JPY</option>
                      <option value="INR" data-id="element-7241">INR</option>
                      <option value="MXN" data-id="element-7242">MXN</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2" data-id="element-7243">
                    <label htmlFor="type" className="block text-sm font-medium text-gray-700" data-id="element-7244">
                      Rate Type
                    </label>
                    <select id="type" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filters.type} onChange={e => handleFilterChange('type', e.target.value)} data-id="element-7245">
                      <option value="" data-id="element-7246">All</option>
                      <option value="Manual" data-id="element-7247">Manual</option>
                      <option value="System" data-id="element-7248">System</option>
                      <option value="Contract-Based" data-id="element-7249">Contract-Based</option>
                      <option value="External" data-id="element-7250">External</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2" data-id="element-7251">
                    <label htmlFor="scope" className="block text-sm font-medium text-gray-700" data-id="element-7252">
                      Scope
                    </label>
                    <select id="scope" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filters.scope} onChange={e => handleFilterChange('scope', e.target.value)} data-id="element-7253">
                      <option value="" data-id="element-7254">All</option>
                      <option value="Global" data-id="element-7255">Global</option>
                      <option value="Entity" data-id="element-7256">Entity</option>
                      <option value="Site" data-id="element-7257">Site</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2" data-id="element-7258">
                    <label htmlFor="status" className="block text-sm font-medium text-gray-700" data-id="element-7259">
                      Status
                    </label>
                    <select id="status" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filters.status} onChange={e => handleFilterChange('status', e.target.value)} data-id="element-7260">
                      <option value="" data-id="element-7261">All</option>
                      <option value="Active" data-id="element-7262">Active</option>
                      <option value="Inactive" data-id="element-7263">Inactive</option>
                    </select>
                  </div>
                </div>
              </div>}

            {/* Action buttons */}
            <div className="px-4 py-3 bg-gray-50 sm:px-6 flex justify-between" data-id="element-7264">
              <div className="flex space-x-2" data-id="element-7265">
                <button onClick={handleAddRate} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7266">
                  <PlusIcon className="h-4 w-4 mr-1" data-id="element-7267" />
                  Add Exchange Rate
                </button>
                <button onClick={() => setShowRateUpload(true)} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7268">
                  <UploadIcon className="h-4 w-4 mr-1" data-id="element-7269" />
                  Upload Rates
                </button>
                <button onClick={handleRunSync} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7270">
                  <RefreshCwIcon className="h-4 w-4 mr-1" data-id="element-7271" />
                  Sync Now
                </button>
              </div>
              <button onClick={handleShowSubscriptionSettings} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7272">
                <SettingsIcon className="h-4 w-4 mr-1" data-id="element-7273" />
                External Provider Settings
              </button>
            </div>

            {/* Exchange Rate Table */}
            <ExchangeRateTable rates={filteredRates} onEdit={handleEditRate} onToggleStatus={handleToggleStatus} onViewHistory={handleViewHistory} locale={locale} data-id="element-7274" />
          </div>
        </div>

        {/* Right column - Forms and detail views */}
        <div className="lg:w-1/3" data-id="element-7275">
          {showRateForm && <ExchangeRateForm rate={selectedRate} onSave={handleSaveRate} onCancel={() => setShowRateForm(false)} locale={locale} data-id="element-7276" />}
          {showSubscriptionForm && <ExternalSubscriptionForm onClose={() => setShowSubscriptionForm(false)} onRunSync={handleRunSync} locale={locale} data-id="element-7277" />}
          {showRateHistory && selectedRate && <RateHistoryView rate={selectedRate} onClose={() => setShowRateHistory(false)} locale={locale} data-id="element-7278" />}
          {showRateUpload && <ExchangeRateUpload onImport={handleImportRates} onCancel={() => setShowRateUpload(false)} locale={locale} data-id="element-7279" />}
          {!showRateForm && !showSubscriptionForm && !showRateHistory && !showRateUpload && <div className="bg-white shadow rounded-lg p-6" data-id="element-7280">
                <div className="text-center py-8" data-id="element-7281">
                  <DollarSignIcon className="h-12 w-12 text-indigo-400 mx-auto mb-4" data-id="element-7282" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-7283">
                    Currency Exchange Management
                  </h3>
                  <p className="text-sm text-gray-500 mb-6" data-id="element-7284">
                    Select an action from the left panel or click the buttons
                    below to get started.
                  </p>
                  <div className="flex flex-col space-y-3" data-id="element-7285">
                    <button onClick={handleAddRate} className="inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7286">
                      <PlusIcon className="h-4 w-4 mr-1" data-id="element-7287" />
                      Add New Exchange Rate
                    </button>
                    <button onClick={() => setShowRateUpload(true)} className="inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7288">
                      <UploadIcon className="h-4 w-4 mr-1" data-id="element-7289" />
                      Upload Exchange Rates
                    </button>
                    <button onClick={handleShowSubscriptionSettings} className="inline-flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7290">
                      <SettingsIcon className="h-4 w-4 mr-1" data-id="element-7291" />
                      Configure External Provider
                    </button>
                  </div>
                </div>
              </div>}
        </div>
      </div>
    </div>;
}